import React, { useMemo, useState, useCallback } from 'react';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import SlDynamicTableHeader, {
  DynamicHeaderConfig
} from 'src/components/BeaconV2Components/EntityTableRefresh/SlDynamicTableHeader';
import { useCreateBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import {
  lifetimeAggregationFields,
  weeklyFields
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewConstants';
import ProductReviewGrid from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/Visualizations/ProductReviewGrid';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { exportReviewProduct } from 'src/components/BeaconV2Components/common/AppExportModal/exportDataHooks';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriod,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import ReviewsV2ProductGridBySales from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSummary/ReviewsV2ProductGridBySales';
import { useLatestCumulativeReviewWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { ExportDropdownOption } from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';

interface DropdownOption {
  id: string;
  label: string;
  subTextLabel?: string;
  isCumulative?: boolean;
}

export interface SelectedGroupByOption {
  displayName: string;
  name: string;
  isSelected: boolean;
}

// Dropdown option display names for the product grid
enum ReviewRatingDisplayName {
  parentWeeklyRatingVolume = 'Weekly Rating Volume',
  weeklyRating = 'Weekly Rating',
  parentLifetimeRatingVolume = 'Cumulative Rating Volume',
  parentLifetimeRating = 'Cumulative Rating',
  weightedParentWeeklyRating = 'Weighted Weekly Rating',
  weightedParentLifetimeRating = 'Weighted Cumulative Rating'
}

/**
 * Dropdown options for the product grid.
 * This determines the text and the dropdown options for the regular [metric] by [product] grid.
 */
const GRID_DROPDOWN_OPTIONS: { [key: string]: DropdownOption[] } = {
  summary: [
    {
      id: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
      label: ReviewRatingDisplayName.parentWeeklyRatingVolume,
      subTextLabel: 'Rating Volume'
    },
    {
      id: RatingsAndReviewMetric.WEEKLY_RATING,
      label: ReviewRatingDisplayName.weeklyRating,
      subTextLabel: 'Rating'
    },
    {
      id: RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME,
      label: ReviewRatingDisplayName.parentLifetimeRatingVolume,
      isCumulative: true
    },
    {
      id: RatingsAndReviewMetric.PARENT_LIFETIME_RATING,
      label: ReviewRatingDisplayName.parentLifetimeRating,
      isCumulative: true
    },
    {
      id: RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME,
      label: 'Weekly Review Volume',
      subTextLabel: 'Review Volume'
    },
    {
      id: RatingsAndReviewMetric.WEEKLY_REVIEW_RATING,
      label: 'Weekly Review Rating',
      subTextLabel: 'Review Rating'
    },
    {
      id: RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME,
      label: 'Cumulative Review Volume',
      isCumulative: true
    },
    {
      id: RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING,
      label: 'Cumulative Review Rating',
      isCumulative: true
    },
    { id: RatingsAndReviewMetric.REVIEW_QUALITY_SCORE, label: 'Review Quality Score' },
    { id: 'weeklyPosSentimentValue', label: 'Positive Sentiment' },
    { id: 'weeklyNeuSentimentValue', label: 'Neutral Sentiment' },
    { id: 'weeklyNegSentimentValue', label: 'Negative Sentiment' }
  ],
  ratings: [
    {
      id: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
      label: ReviewRatingDisplayName.parentWeeklyRatingVolume,
      subTextLabel: 'Rating Volume'
    },
    {
      id: RatingsAndReviewMetric.WEEKLY_RATING,
      label: ReviewRatingDisplayName.weeklyRating,
      subTextLabel: 'Rating'
    },
    {
      id: RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME,
      label: ReviewRatingDisplayName.parentLifetimeRatingVolume,
      isCumulative: true
    },
    {
      id: RatingsAndReviewMetric.PARENT_LIFETIME_RATING,
      label: ReviewRatingDisplayName.parentLifetimeRating,
      isCumulative: true
    }
  ],
  reviews: [
    {
      id: RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME,
      label: 'Weekly Review Volume',
      subTextLabel: 'Review Volume'
    },
    {
      id: RatingsAndReviewMetric.WEEKLY_REVIEW_RATING,
      label: 'Weekly Review Rating',
      subTextLabel: 'Review Rating'
    },
    {
      id: RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME,
      label: 'Cumulative Review Volume',
      isCumulative: true
    },
    {
      id: RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING,
      label: 'Cumulative Review Rating',
      isCumulative: true
    },

    { id: RatingsAndReviewMetric.REVIEW_QUALITY_SCORE, label: 'Review Quality Score' }
  ],
  sentiment: [
    {
      id: 'weeklyPosSentimentValue',
      label: 'Positive Sentiment'
    },
    {
      id: 'weeklyNeuSentimentValue',
      label: 'Neutral Sentiment'
    },
    {
      id: 'weeklyNegSentimentValue',
      label: 'Negative Sentiment'
    }
  ]
};

// Fields for the [ratings] product grid metrics
const ratingsFields = {
  [RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME]: [
    {
      name: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
      displayName: 'Weekly Rating Volume'
    }
  ],
  [RatingsAndReviewMetric.WEEKLY_RATING]: [
    {
      name: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
      displayName: 'Weekly Rating Volume'
    },
    {
      name: RatingsAndReviewMetric.WEIGHTED_PARENT_WEEKLY_RATING,
      displayName: 'Weighted Weekly Rating',
      displayInTable: false
    },
    {
      name: RatingsAndReviewMetric.WEEKLY_RATING,
      displayName: 'Weekly Rating'
    }
  ],
  [RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME]: [
    {
      name: RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME,
      displayName: 'Cumulative Rating Volume'
    }
  ],
  [RatingsAndReviewMetric.PARENT_LIFETIME_RATING]: [
    {
      name: RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME,
      displayName: 'Cumulative Rating Volume'
    },
    {
      name: RatingsAndReviewMetric.WEIGHTED_PARENT_LIFETIME_RATING,
      displayName: 'Weighted Cumulative Rating',
      displayInTable: false
    },
    {
      name: RatingsAndReviewMetric.PARENT_LIFETIME_RATING,
      displayName: 'Cumulative Rating'
    }
  ]
};

// Fields for the [reviews] product grid metrics
const reviewsFields = {
  [RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME]: [
    {
      name: RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME,
      displayName: 'Weekly Review Volume'
    }
  ],
  [RatingsAndReviewMetric.WEEKLY_REVIEW_RATING]: [
    {
      name: RatingsAndReviewMetric.WEEKLY_REVIEW_RATING,
      displayName: 'Weekly Review Rating'
    },
    {
      name: 'weightedWeeklyReviewRating',
      displayName: 'Weighted Weekly Review Rating',
      displayInTable: false
    },
    {
      name: 'weeklyReviewVolume',
      displayName: 'Weekly Review Volume'
    }
  ],
  [RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME]: [
    {
      name: RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME,
      displayName: 'Cumulative Review Volume'
    }
  ],

  [RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING]: [
    {
      name: 'lifetimeReviewVolume',
      displayName: 'Cumulative Review Volume'
    },
    {
      name: 'weightedLifetimeReviewRating',
      displayName: 'Weighted Cumulative Review Rating',
      displayInTable: false
    },
    {
      name: RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING,
      displayName: 'Cumulative Review Rating'
    }
  ],

  [RatingsAndReviewMetric.REVIEW_QUALITY_SCORE]: [
    { name: RatingsAndReviewMetric.REVIEW_QUALITY_SCORE, displayName: 'Review Quality Score' }
  ]
};

const sentimentField = {
  weeklyPosSentimentValue: [{ name: 'weeklyPosSentimentValue', displayName: 'Weekly Positive Sentiment' }],
  weeklyNeuSentimentValue: [{ name: 'weeklyNeuSentimentValue', displayName: 'Weekly Neutral Sentiment' }],
  weeklyNegSentimentValue: [{ name: 'weeklyNegSentimentValue', displayName: 'Weekly Negative Sentiment' }]
};

// Dropdown options for determining which table to display
const componentDropdownOptions = [
  {
    id: 'product',
    label: 'Product'
  },
  {
    id: 'review',
    label: 'Review'
  }
];

const fields = {
  ratings: {
    [RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME]: [
      {
        name: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
        displayName: ReviewRatingDisplayName.parentWeeklyRatingVolume
      }
    ],
    [RatingsAndReviewMetric.WEEKLY_RATING]: [
      {
        name: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
        displayName: ReviewRatingDisplayName.parentWeeklyRatingVolume
      },
      {
        name: RatingsAndReviewMetric.WEIGHTED_PARENT_WEEKLY_RATING,
        displayName: ReviewRatingDisplayName.weightedParentWeeklyRating,
        displayInTable: false
      },
      {
        name: RatingsAndReviewMetric.WEEKLY_RATING,
        displayName: ReviewRatingDisplayName.weeklyRating
      }
    ],
    [RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME]: [
      {
        name: RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME,
        displayName: ReviewRatingDisplayName.parentLifetimeRatingVolume
      }
    ],

    [RatingsAndReviewMetric.PARENT_LIFETIME_RATING]: [
      {
        name: RatingsAndReviewMetric.PARENT_LIFETIME_RATING_VOLUME,
        displayName: ReviewRatingDisplayName.parentLifetimeRatingVolume
      },
      {
        name: RatingsAndReviewMetric.WEIGHTED_PARENT_LIFETIME_RATING,
        displayName: ReviewRatingDisplayName.weightedParentLifetimeRating,
        displayInTable: false
      },
      {
        name: RatingsAndReviewMetric.PARENT_LIFETIME_RATING,
        displayName: ReviewRatingDisplayName.parentLifetimeRating
      }
    ]
  },

  reviews: {
    [RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME]: [
      {
        name: RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME,
        displayName: 'Weekly Review Volume'
      }
    ],
    [RatingsAndReviewMetric.WEEKLY_REVIEW_RATING]: [
      {
        name: RatingsAndReviewMetric.WEEKLY_REVIEW_RATING,
        displayName: 'Weekly Review Rating'
      },
      {
        name: RatingsAndReviewMetric.WEIGHTED_WEEKLY_REVIEW_RATING,
        displayName: 'Weighted Weekly Review Rating',
        displayInTable: false
      },
      {
        name: RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME,
        displayName: 'Weekly Review Volume'
      }
    ],
    [RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME]: [
      {
        name: RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME,
        displayName: 'Cumulative Review Volume'
      }
    ],

    [RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING]: [
      {
        name: RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME,
        displayName: 'Cumulative Review Volume'
      },
      {
        name: RatingsAndReviewMetric.WEIGHTED_LIFETIME_REVIEW_RATING,
        displayName: 'Weighted Cumulative Review Rating',
        displayInTable: false
      },
      {
        name: RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING,
        displayName: 'Cumulative Review Rating'
      }
    ],
    [RatingsAndReviewMetric.REVIEW_QUALITY_SCORE]: [
      { name: RatingsAndReviewMetric.REVIEW_QUALITY_SCORE, displayName: 'Review Quality Score' }
    ]
  },

  sentiment: { ...sentimentField },

  summary: {
    ...ratingsFields,
    ...reviewsFields,
    ...sentimentField
  }
};

const oldIndexNameToUse = [RatingsAndReviewMetric.REVIEW_QUALITY_SCORE];
/**
 * Product grid that is used for review summary and ratings tab.
 * We disable the group by dropdown on the ratings tab.
 */
const ReviewSummaryProductGrid = ({
  metric = 'ratings',
  disableGroupBy,
  tableComponent = 'product'
}: {
  metric?: 'summary' | 'ratings' | 'reviews' | 'sentiment';
  disableGroupBy?: boolean;
  tableComponent?: 'product' | 'review';
}) => {
  const fieldsToUse = fields[metric];

  // State for the selected metric in the product grid
  const [selectedTableOption, setSelectedTableOption] = useState(
    GRID_DROPDOWN_OPTIONS[metric][0].id as RatingsAndReviewMetric
  );
  const { type: mainEntityType, name: mainEntityName } = useMainEntity();

  const initialSelectedTableComponent = useMemo(
    () => (tableComponent === 'product' ? componentDropdownOptions[0] : componentDropdownOptions[1]),
    [tableComponent]
  );
  const [selectedTableComponent, setSelectedTableComponent] = useState(initialSelectedTableComponent);

  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const { startWeek, endWeek } = useMainTimePeriod();
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const createRequestBuilder = useCreateBaseMetricRequestBuilder();

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  // The subtitle text changes based on the selected table component and metric option

  const subtitleText = useMemo(() => {
    const selectedDisplayData = GRID_DROPDOWN_OPTIONS[metric].find((option) => option.id === selectedTableOption);
    const selectedMainLabel = selectedDisplayData?.subTextLabel || selectedDisplayData?.label;
    const isCumulativeData = selectedDisplayData?.isCumulative;

    switch (selectedTableComponent.id) {
      case 'product':
        return (
          <BeaconSubtitleContainer>
            <Text variant="body1">
              {`The table displays the ${selectedMainLabel?.toLocaleLowerCase()} for ${entityPhrase} `}
              {taxonomyText}
              {isCumulativeData
                ? ` by product.`
                : ` by product during the ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
              <SubtitleFilters />
            </Text>
          </BeaconSubtitleContainer>
        );

      case 'review':
        return (
          <BeaconSubtitleContainer>
            <Text variant="body1">
              {`The table displays the key metrics for ${entityPhrase} `}
              {taxonomyText}
              {` by review.`}
              <SubtitleFilters />
            </Text>
          </BeaconSubtitleContainer>
        );
      default:
        return '';
    }
  }, [
    taxonomyText,
    entityPhrase,
    selectedTableComponent,
    selectedTableOption,
    mainTimePeriodShortDisplayName,
    comparisonTimePeriodShortDisplayName,
    metric
  ]);

  /**
   * Configuration for the dynamic table header dropdown options.
   * This determines the text and the dropdown options for both the product grid and the reviews grid.
   *
   * */
  const tableHeaderConfig: DynamicHeaderConfig[] = useMemo(() => {
    switch (selectedTableComponent.id) {
      case 'product':
        return [
          {
            displayName: 'Weekly Rating Volume',
            displayType: 'menu',
            dropdownConfig: {
              dropdownOptions: GRID_DROPDOWN_OPTIONS[metric],
              handleDropdownChange: (option) => {
                setSelectedTableOption(option.id as ReviewRatingOption);
              },
              selectedDropdownOption: {
                id: selectedTableOption,
                label: ReviewRatingDisplayName[selectedTableOption]
              }
            }
          },
          {
            displayName: 'by',
            displayType: 'text'
          },
          {
            displayName: 'Product',
            displayType: disableGroupBy ? 'text' : 'menu',
            dropdownConfig: {
              dropdownOptions: componentDropdownOptions,
              handleDropdownChange(option) {
                setSelectedTableComponent(option);
              },
              selectedDropdownOption: selectedTableComponent
            }
          }
        ];
      case 'review':
        return [
          {
            displayName: 'Key Metrics',
            displayType: 'text'
          },
          {
            displayName: 'by',
            displayType: 'text'
          },
          {
            displayName: 'Review',
            displayType: disableGroupBy ? 'text' : 'menu',
            dropdownConfig: {
              dropdownOptions: componentDropdownOptions,
              handleDropdownChange(option) {
                setSelectedTableComponent(option);
              },
              selectedDropdownOption: selectedTableComponent
            }
          }
        ];
      default:
        return null;
    }
  }, [selectedTableOption, selectedTableComponent, disableGroupBy, metric]);

  /**
   * When the selected table component (groupBy) changes (Review/Product), we sort the AppExportModal dropdown options
   * so that the groupBy option from header is always at the top of the list in the AppExportModal.
   * This will work even in the future there are more than 2 options.
   */
  const exportModalDropdownOptionsSortFn = useCallback(
    (options: ExportDropdownOption[]): ExportDropdownOption[] => {
      return options.sort((a, _) =>
        a.label.toLocaleLowerCase() === selectedTableComponent?.label.toLocaleLowerCase() ? -1 : 1
      );
    },
    [selectedTableComponent]
  );

  return (
    <>
      <Flex flexDirection="column">
        <EntityTableHeader
          title=""
          paddingTop={0}
          paddingBottom={0}
          enableSwitchingLayouts
          iconsList={[IconsList.export]}
          subTitle={() => {
            return subtitleText;
          }}
          renderHeader={() => <SlDynamicTableHeader config={tableHeaderConfig} />}
          exportOptionsSortFn={exportModalDropdownOptionsSortFn}
          buildExportRequest={({ groupByFieldName, includeCompTimePeriod, originalRequest }) => {
            // original request is coming from the Beacon V1 Review/ Review Trends, so we are just replacing it here.
            const isSeparateByWeek = groupByFieldName.includes('weekId');

            if (groupByFieldName.includes('reviews')) {
              const [reviewExport] = originalRequest;

              return [
                {
                  ...reviewExport,
                  aggregations: null,
                  conditions: {
                    computeFilters: [],
                    nestedFilterConditions: [],
                    condition: 'must',
                    rangeFilters: [
                      ...reviewExport.conditions.rangeFilters,
                      {
                        fieldName: 'weekId',
                        maxValue: endWeek,
                        minValue: startWeek
                      }
                    ],
                    termFilters: [
                      ...reviewExport.conditions.termFilters,
                      { condition: 'should', fieldName: 'reviewText', values: [''] },
                      {
                        condition: 'should',
                        fieldName: 'stars',
                        values: ['1', '2', '3', '4', '5']
                      }
                    ]
                  },
                  sortFilter: {
                    sortFields: [
                      {
                        fieldName: 'reviewSentiment',
                        direction: 'desc'
                      }
                    ]
                  },
                  searchType: 'beacon-review-metrics',
                  returnDocuments: true,
                  returnParentDocuments: true,
                  searchBy: 'child',
                  doAggregation: false
                }
              ];
            }

            return exportReviewProduct({
              groupByFieldName,
              includeCompTimePeriod,
              originalRequest,
              startWeek,
              endWeek,
              latestCumulativeWeekId,
              comparisonStartWeek,
              comparisonEndWeek,
              mainEntityName,
              mainEntityType,
              createRequestBuilder: createRequestBuilder({
                requestId: 'entityGridMetricsentitypagecontainer_trendingProducts_5-product--1',
                indexName: BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2,
                startWeekId: startWeek,
                endWeekId: endWeek,
                fields: weeklyFields,
                sortFieldName: RatingsAndReviewMetric.PARENT_WEEKLY_RATING_VOLUME,
                buildAggregationBuilder: (aggBuilder) => {
                  if (includeCompTimePeriod) {
                    aggBuilder.replaceComparisonRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek);
                  }

                  if (isSeparateByWeek) {
                    lifetimeAggregationFields.forEach((fieldName) =>
                      aggBuilder.addAggregationFieldsByIndexAndField(
                        BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2,
                        fieldName
                      )
                    );
                  }

                  return aggBuilder.setSortDirection('desc').setGroupByFieldName(groupByFieldName);
                }
              })
            });
          }}
        />

        {selectedTableComponent.id === 'review' ? <ProductReviewGrid /> : null}

        {selectedTableComponent.id === 'product' ? (
          <ReviewsV2ProductGridBySales
            showHeader={false}
            indexName={
              oldIndexNameToUse.includes(selectedTableOption)
                ? BEACON_INDEX_NAMES.REVIEWS_METRICS
                : BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2
            }
            sortFieldName={selectedTableOption}
            mainFieldName={selectedTableOption}
            fields={fieldsToUse[selectedTableOption]}
            selectedTableOption={selectedTableOption}
          />
        ) : null}
      </Flex>
    </>
  );
};

export default ReviewSummaryProductGrid;
